import { useMemo } from 'react'
import type { Task, Category, TaskStatus, TaskPriority } from '@/lib/types'

interface TaskFilterOptions {
  categoryId?: number | null
  categories?: Category[]
  status?: TaskStatus
  searchQuery?: string
  // NL filter extensions
  dateRange?: { start: string; end: string }
  priority?: TaskPriority
}

export function useTaskFilters(tasks: Task[], options: TaskFilterOptions) {
  const { categoryId, categories, status, searchQuery, dateRange, priority } = options

  const filteredTasks = useMemo(() => {
    let result = tasks

    // Filter by category (include children)
    if (categoryId !== undefined && categoryId !== null) {
      const childIds = categories
        ?.filter(c => c.parentId === categoryId)
        .map(c => c.id) || []

      const targetIds = new Set([categoryId, ...childIds])

      result = result.filter((task) =>
        task.categoryId !== null && targetIds.has(task.categoryId)
      )
    }

    // Filter by status
    if (status) {
      result = result.filter((task) => task.status === status)
    }

    // Filter by priority
    if (priority) {
      result = result.filter((task) => task.priority === priority)
    }

    // Filter by date range
    if (dateRange) {
      result = result.filter((task) => {
        if (!task.dueDate) return false
        return task.dueDate >= dateRange.start && task.dueDate <= dateRange.end
      })
    }

    // Filter by search query
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase()
      result = result.filter(
        (task) =>
          task.title.toLowerCase().includes(query) ||
          task.memo?.toLowerCase().includes(query) ||
          task.subtasks.some((st) => st.title.toLowerCase().includes(query))
      )
    }

    return result
  }, [tasks, categoryId, categories, status, searchQuery, dateRange, priority])

  return filteredTasks
}
